<template>
  <transition name="last-login-recommend-fade">
    <div
      v-if="visible && account"
      class="last-login-recommend"
      role="dialog"
      aria-label="前回のログイン方法のおすすめ"
    >
      <button
        class="last-login-recommend__close"
        type="button"
        aria-label="閉じる"
        @click="onDismiss"
      >
        ×
      </button>
      <p class="last-login-recommend__title">
        前回は{{ providerLabel }}でログインしました
      </p>
      <div class="last-login-recommend__account">
        <img
          v-if="account.photoURL"
          :src="account.photoURL"
          class="last-login-recommend__avatar"
          alt=""
          referrerpolicy="no-referrer"
        />
        <span
          v-else
          class="last-login-recommend__avatar last-login-recommend__avatar--placeholder"
          >{{ avatarInitial }}</span
        >
        <span class="last-login-recommend__meta">
          <span class="last-login-recommend__name">{{ accountName }}</span>
          <span v-if="maskedEmail" class="last-login-recommend__email">{{
            maskedEmail
          }}</span>
        </span>
      </div>
      <button
        class="last-login-recommend__cta"
        type="button"
        @click="onContinue"
      >
        {{ ctaLabel }}
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * 前回ログインアカウントの右上レコメンドカード（自前 UI）。
 * App.vue には組み込まず、main.js から独立ルートとしてマウントされる。
 *
 * 表示の使い分け（前回ログインが）:
 * - Google または記録なし → まず Google One Tap（純正プロンプト）。出なければこのカード
 * - Google 以外 → このカードのみ
 * - PDF レンダリング対象の preview / print 系ルートでは出さない（出力物への写り込み防止）
 */
import { computed, onMounted, ref, watch } from 'vue'
import router from '@/router'
import { useAuth } from '@/composables/useAuth'
import {
  cancelOneTapPrompt,
  useGoogleOneTap
} from '@/composables/useGoogleOneTap'
import { useLastLoginAccount } from '@/composables/useLastLoginAccount'
import { useLoginPostProcess } from '@/composables/useLoginPostProcess'
import {
  createAppleAuthProvider,
  createFacebookAuthProvider,
  createGoogleAuthProvider,
  getFirebaseAuth,
  signInWithPopup,
  type AuthProvider
} from '@/infra/firebase/auth'
import { getFirebaseClient } from '@/plugins/firebase-lib'
import {
  emitter,
  OPEN_LOGIN_DIALOG,
  emitOpenLoginDialog
} from '@/plugins/emitter'
import { PROVIDER_LABELS, maskEmail } from '@/utils/auth/lastLoginAccount'

/** 出力物へ写り込む恐れのあるルート（PDF/スクリーンショットは preview 系を開く） */
const EXCLUDED_PATH_PATTERN = /preview|print/i

/**
 * FedCM ではモーメント通知（isNotDisplayed 等）が来ないことがあるため、
 * 一定時間ログインが成立しなければカードへフォールバックする
 */
const ONE_TAP_FALLBACK_TIMEOUT_MS = 4000
const LOGIN_PAGE_ROOT_SELECTOR = '#renew2-login'
const EMAIL_PREFILL_RETRY_INTERVAL_MS = 50
const EMAIL_PREFILL_MAX_ATTEMPTS = 40

const { user, whenAuthReady } = useAuth()
const { account, isDismissed, dismiss } = useLastLoginAccount()
const { isConfigured, promptOneTap } = useGoogleOneTap()
const { runLoginPostProcess } = useLoginPostProcess()

const currentPath = ref(router.currentRoute.path)
router.afterEach((to) => {
  currentPath.value = to.path
})

const cardActivated = ref(false)

const isExcludedRoute = computed(() =>
  EXCLUDED_PATH_PATTERN.test(currentPath.value)
)

const visible = computed(
  () =>
    cardActivated.value &&
    !user.value &&
    !!account.value &&
    !isDismissed.value &&
    !isExcludedRoute.value
)

const providerLabel = computed(() =>
  account.value ? PROVIDER_LABELS[account.value.provider] : ''
)

const accountName = computed(
  () => account.value?.displayName || providerLabel.value
)

const maskedEmail = computed(() => maskEmail(account.value?.email || ''))

const avatarInitial = computed(() => {
  const source = account.value?.displayName || account.value?.email || ''
  return source ? source.slice(0, 1).toUpperCase() : '?'
})

const ctaLabel = computed(() =>
  account.value?.displayName
    ? `${account.value.displayName} として続行`
    : `${providerLabel.value}でログイン`
)

function sendRecommendEvent(action: 'impression' | 'click' | 'dismiss'): void {
  const params = { method: account.value?.provider || 'none' }
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void
    dataLayer?: Record<string, unknown>[]
  }
  // GA4 へ直接送る（GTM 側のタグ設定を待たずに数値が取れる。sendAuthEvent と同じ方式）
  w.gtag?.('event', `last_login_recommend_${action}`, params)
  // GTM ロード前に impression が発火しても失わないよう、無ければ先に作る（GTM 標準イディオム）
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = []
  }
  w.dataLayer.push({ event: `last_login_recommend_${action}`, ...params })
}

let impressionSent = false

function activateCard(): void {
  // カードは「前回ログインの記録」がある人専用。記録なしユーザーへの One Tap は
  // 獲得目的のおまけで、不発時に代わりに出すものは無い（generic なログイン訴求は対象外）
  if (!account.value || isDismissed.value) return
  cardActivated.value = true
}

watch(visible, (next) => {
  if (next && !impressionSent) {
    impressionSent = true
    sendRecommendEvent('impression')
  }
})

type SignInClient = {
  signInGoogle?: () => void
  signInApple?: () => void
  signInLine?: () => void
  signInFacebook?: () => void
  $gtm?: {
    hintAuthMethod?: (method: string) => void
    clearAuthMethodHint?: () => void
  }
}

/**
 * authDomain とアプリのオリジンが異なる環境（preview channel 等）では、
 * Chrome のサードパーティストレージ分離で signInWithRedirect の結果受け渡しが
 * 壊れるため、window.postMessage で完結する signInWithPopup へ切り替える。
 * 本番・共有 STG は同一オリジンなので既存どおり redirect を使う
 */
const usesPopupFallback =
  window.location.hostname !== process.env.VUE_APP_FIREBASE_AUTHDOMAIN

function popupProviderFor(
  provider: 'google' | 'apple' | 'facebook'
): AuthProvider {
  if (provider === 'apple') {
    return createAppleAuthProvider()
  }
  if (provider === 'facebook') {
    return createFacebookAuthProvider()
  }
  return createGoogleAuthProvider()
}

async function signInViaPopup(
  provider: 'google' | 'apple' | 'facebook'
): Promise<void> {
  const client = getFirebaseClient() as SignInClient | null
  client?.$gtm?.hintAuthMethod?.(provider)
  try {
    const result = await signInWithPopup(
      getFirebaseAuth(),
      popupProviderFor(provider)
    )
    if (result.user) {
      // redirect を通らないため getRedirectResult 相当の後処理を明示的に行う
      await runLoginPostProcess(result.user)
    }
  } catch (e) {
    // 残ったヒントは次回別手段のログインを誤計上させるため捨てる
    client?.$gtm?.clearAuthMethodHint?.()
    console.error('[LastLoginRecommendCard] signInWithPopup failed', e)
  }
}

function onContinue(): void {
  const current = account.value
  if (!current) return
  sendRecommendEvent('click')
  const client = getFirebaseClient() as SignInClient | null
  switch (current.provider) {
    case 'google':
      if (usesPopupFallback) {
        signInViaPopup('google')
      } else {
        client?.signInGoogle?.()
      }
      break
    case 'apple':
      if (usesPopupFallback) {
        signInViaPopup('apple')
      } else {
        client?.signInApple?.()
      }
      break
    case 'line':
      // LINE はカスタムトークン経路（location.href）のため popup 代替不可。
      // preview channel では LINE ログインは検証できない（本番・共有 STG では動く）
      client?.signInLine?.()
      break
    case 'facebook':
      if (usesPopupFallback) {
        signInViaPopup('facebook')
      } else {
        client?.signInFacebook?.()
      }
      break
    case 'password':
      openLoginDialogOrNavigate(current.email)
      break
  }
}

function prefillEmailLoginForm(email: string, rootSelector: string): void {
  // LoginDialog や遅延ロードされた /login の描画完了まで短時間待つ。
  const tryPrefill = (attempt: number): void => {
    const root = document.querySelector<HTMLElement>(rootSelector)
    const emailInput = root?.querySelector<HTMLInputElement>(
      'input[type="email"][name="mail-link-input"]'
    )
    if (!emailInput) {
      if (attempt < EMAIL_PREFILL_MAX_ATTEMPTS) {
        window.setTimeout(
          () => tryPrefill(attempt + 1),
          EMAIL_PREFILL_RETRY_INTERVAL_MS
        )
      }
      return
    }

    if (email) {
      // DOM の値だけでなく input イベントも発火し、LoginSpecialForms の
      // v-model とメール登録状況の確認処理へ反映する。
      emailInput.value = email
      emailInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    const passwordInput = root.querySelector<HTMLInputElement>(
      'input[type="password"][name="mail-link-input"]'
    )
    const focusTarget = passwordInput || emailInput
    focusTarget.focus()
  }

  window.setTimeout(() => tryPrefill(0), 0)
}

/** 現在ページに LoginDialog が居ればダイアログを開き、居なければ公開ログイン画面へ移動する */
function openLoginDialogOrNavigate(email: string): void {
  const listeners = emitter.all.get(OPEN_LOGIN_DIALOG)
  if (listeners && listeners.length > 0) {
    emitOpenLoginDialog({ trigger: 'last_login_recommend' })
    prefillEmailLoginForm(email, '.login-dialog__content')
    return
  }

  // /mypage は認証必須のため、未ログイン状態ではルートガードに /login へ戻される。
  // /login 上で押すと見かけ上何も起きないので、最初から公開ログイン画面へ寄せる。
  cardActivated.value = false
  if (currentPath.value === '/login') {
    prefillEmailLoginForm(email, LOGIN_PAGE_ROOT_SELECTOR)
    return
  }
  router
    .push('/login')
    .then(() => prefillEmailLoginForm(email, LOGIN_PAGE_ROOT_SELECTOR))
    .catch(() => prefillEmailLoginForm(email, LOGIN_PAGE_ROOT_SELECTOR))
}

function onDismiss(): void {
  sendRecommendEvent('dismiss')
  dismiss()
}

let flowStarted = false

async function startRecommendFlow(): Promise<void> {
  if (flowStarted) return
  flowStarted = true

  // watch 経由の起動でも認証状態が確定してから判定する
  await whenAuthReady()
  if (user.value) return

  // 直接ロード時、router の初期ルート確定（非同期ガード）が authReady より遅れると
  // onMounted 時点では '/' に見える。ここで再判定し、除外なら watch からの再起動に任せる
  if (isExcludedRoute.value) {
    flowStarted = false
    return
  }

  // ×で閉じた直後の再表示はカードだけでなく One Tap も抑止する（7日クールダウン）
  if (isDismissed.value) return

  const current = account.value
  const shouldTryOneTap =
    (!current || current.provider === 'google') && isConfigured()

  if (shouldTryOneTap) {
    const result = await promptOneTap({
      onNotShown: activateCard,
      onSignInError: activateCard
    })
    if (result === 'unavailable') {
      activateCard()
      return
    }
    // FedCM でモーメント通知が来なかった場合の保険。One Tap と同時に出ても
    // CTA は同じ行き先で、One Tap 側が上に重なるだけなので実害はない
    window.setTimeout(() => {
      if (!user.value) activateCard()
    }, ONE_TAP_FALLBACK_TIMEOUT_MS)
    return
  }
  activateCard()
}

// preview/print 系ルートで開始した場合は、通常ルートへ遷移したときに起動する。
// 逆に除外ルートへ入ったら、表示中の One Tap プロンプトも閉じる（出力物への写り込み防止）
watch(isExcludedRoute, (excluded) => {
  if (excluded) {
    cancelOneTapPrompt()
    return
  }
  if (!flowStarted) {
    startRecommendFlow()
  }
})

onMounted(() => {
  if (isExcludedRoute.value) return
  startRecommendFlow()
})
</script>

<style lang="scss" scoped>
.last-login-recommend {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1000;
  box-sizing: border-box;
  width: min(320px, calc(100vw - 24px));
  padding: 16px;
  border-radius: 12px;
  background: #202124;
  color: #fff;
  box-shadow: 0 4px 16px rgb(0 0 0 / 30%);
  font-size: 14px;
  text-align: left;

  &__close {
    position: absolute;
    top: 6px;
    right: 8px;
    border: none;
    background: none;
    color: #9aa0a6;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #fff;
    }
  }

  &__title {
    margin: 0 16px 12px 0;
    font-size: 13px;
    color: #e8eaed;
  }

  &__account {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    object-fit: cover;

    &--placeholder {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #5f6368;
      color: #fff;
      font-weight: 600;
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    color: #9aa0a6;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__cta {
    display: block;
    width: 100%;
    border: none;
    border-radius: 20px;
    padding: 9px 16px;
    background: #1a73e8;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: #1765cc;
    }
  }
}

.last-login-recommend-fade-enter-active,
.last-login-recommend-fade-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.last-login-recommend-fade-enter,
.last-login-recommend-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
